import React, { useCallback, useEffect } from 'react';

import { useXoeDictation } from '../../hooks/xoe/useXoeDictation';
import { useXoeRailState } from '../../hooks/xoe/useXoeRailState';
import { useXoeComposerInsert } from '../../hooks/xoe/useXoeComposerInsert';
import { confirmDeleteConversation } from '../../utils/xoe/confirmDeleteConversation';
import { useReviewMemoryProposals } from './MemoryProposalsChip';
import XoeChatHeader from './XoeChatHeader';
import XoeComposer from './XoeComposer';
import XoeConversationRail from './XoeConversationRail';
import XoeExpertSelector from './XoeExpertSelector';
import XoeHomeScreen from './XoeHomeScreen';
import XoeSendFailureNotice from './XoeSendFailureNotice';
import XoeSurface from './XoeSurface';
import XoeThread from './XoeThread';
import type { XoeChatProps } from '../../types/xoe/XoeUiTypes';

const XoeChat: React.FC<XoeChatProps> = ({
	session,
	tone,
	title = 'Xoe',
	railPersistKey,
	railDefaultCollapsed = false,
	onClose,
	closeVariant,
	onMaximise,
	headerNotice,
	homeHeading,
	unavailable,
	onRailCollapsedChange,
	className,
	frameTestId,
}) => {
	const dictation = useXoeDictation();
	const rail = useXoeRailState({ session, persistKey: railPersistKey, defaultCollapsed: railDefaultCollapsed });
	const composerInsert = useXoeComposerInsert();
	const handleReviewMemory = useReviewMemoryProposals(session.acknowledgeMemoryProposals);

	useEffect(() => {
		onRailCollapsedChange?.(rail.collapsed);
	}, [rail.collapsed, onRailCollapsedChange]);

	const handleDelete = useCallback(() => {
		if (session.canDelete) confirmDeleteConversation(() => session.deleteActiveConversation());
	}, [session]);

	const detail = session.activeDetail;
	const isPending = session.turn.isPending;
	const isLoading = !!session.activeUuid && detail === undefined;
	const messages = detail?.messages.data ?? [];
	// Never while loading — that would flash a greeting over a conversation about to appear.
	const showHome = !isLoading && messages.length === 0 && !session.turn.pendingText && !isPending;

	return (
		<XoeSurface className={className} data-testid="xoe-assistant">
			<div className="mg-xoe-frame" data-testid={frameTestId}>
				<XoeChatHeader
					tone={tone}
					title={title}
					boundPatientName={session.boundPatientName}
					pendingMemoryProposals={session.pendingMemoryProposals}
					onReviewMemory={handleReviewMemory}
					rail={{ collapsed: rail.collapsed, onToggle: rail.toggle }}
					onNewConversation={session.startNewConversation}
					onNewGeneralConversation={session.startNewGeneralConversation}
					showNewGeneral={session.focusPatientId != null}
					onDelete={handleDelete}
					canDelete={session.canDelete}
					mic={{ active: dictation.micActive, supported: dictation.micSupported, onToggle: dictation.toggleMic }}
					onMaximise={onMaximise}
					maximiseDisabled={isPending}
					onClose={onClose}
					closeVariant={closeVariant}
					notice={headerNotice}
					extra={
						<XoeExpertSelector
							expertId={session.expertId}
							onChange={session.selectExpert}
							disabled={isPending}
						/>
					}
				/>

				{unavailable ? (
					<XoeHomeScreen
						conversationUuid={null}
						patientLabel={null}
						onInsert={composerInsert.insert}
						unavailable={unavailable}
					/>
				) : (
					<div className="mg-xoe-body">
						<XoeConversationRail
							activeUuid={session.activeUuid}
							focusPatientId={session.focusPatientId}
							collapsed={rail.collapsed}
							onToggleCollapsed={rail.toggle}
							onSelect={session.selectConversation}
							onNewChat={session.startNewConversation}
							onRename={rail.rename}
							onArchive={rail.archive}
							onDelete={rail.remove}
							undoUuid={rail.undoUuid}
							onUndoArchive={rail.undo}
						/>

						<div className="mg-xoe-main">
							{showHome ? (
								<XoeHomeScreen
									conversationUuid={session.activeUuid}
									patientLabel={session.boundPatientName}
									onInsert={composerInsert.insert}
									heading={homeHeading}
								/>
							) : (
								<XoeThread
									messages={messages}
									isLoading={isLoading}
									patientId={detail?.conversation.patient_id ?? null}
									conversationUuid={session.activeUuid ?? ''}
									isPending={isPending}
									pendingText={session.turn.pendingText}
									progress={session.turn.progress}
									onCancel={session.turn.cancel}
									onAutoContinue={session.send}
								/>
							)}

							<div className="mg-xoe-dock">
								<div className="mg-xoe-column">
									{session.sendFailure && (
										<XoeSendFailureNotice
											message={session.sendFailure.message}
											onRetry={session.sendFailure.retry}
											onDismiss={session.sendFailure.dismiss}
										/>
									)}
									<XoeComposer
										onSend={session.send}
										disabled={isPending}
										conversationUuid={session.activeUuid ?? undefined}
										dictatedText={dictation.dictatedText}
										dictationToken={dictation.dictationToken}
										insertedText={composerInsert.insertedText}
										insertToken={composerInsert.insertToken}
									/>
								</div>
							</div>
						</div>
					</div>
				)}
			</div>
		</XoeSurface>
	);
};

export default XoeChat;
